// Shared model for Automation Rules.
//
// Automation Rules is the detection engine, not a tagging tool. Several roadmap
// features are the same shape — watch a member for a condition, then surface or
// do something — and are intended to sit on this rather than build their own
// detection: At-Risk Detection, Moments to Reach Out, Action Required Queue,
// Upcoming Renewals — Attention Rule, Product Replace — Automation Triggers.
//
// Both the rules list and the rule builder read from this file so the two
// screens describe the same object.

export type AutomationCategory = 'Uncategorised' | 'Lifecycle' | 'Behaviour' | 'Value' | 'Product' | 'Seasonal';
export type RuleStatus = 'active' | 'testing' | 'paused' | 'draft';
export type TriggerType = 'Event-based' | 'Scheduled (Daily)';
export type ConditionLogic = 'AND' | 'OR';
export type RunMode = 'once' | 'every';

// A match rule is yes/no. A scored rule weights each condition and maps the
// total to bands, each band with its own actions. Scored is what lets At-Risk
// Detection be a rule rather than a separate engine — and lets a winery build
// its own score instead of only getting ours.
export type RuleType = 'match' | 'scored';

export interface RuleCondition {
  id: string;
  field: string;
  operator: string;
  value: string;
  value2?: string; // second value, used by "is between"
  unit?: string; // used by relative-date and duration fields
  weight?: number; // scored rules only
}

export interface RuleConditionGroup {
  id: string;
  logic: ConditionLogic;
  conditions: RuleCondition[];
}

export interface RuleAction {
  id: string;
  type: string;
  value: string;
  secondaryValue?: string;
}

export interface ScoreBand {
  id: string;
  label: string;
  min: number;
  max: number;
  colour: 'green' | 'amber' | 'orange' | 'red';
  actions: RuleAction[];
}

export interface RuleRun {
  id: string;
  date: string;
  mode: 'live' | 'test';
  matched: number;
  actioned: number;
  heldByCap: number;
}

export interface RuleSafety {
  // Nothing runs unbounded. A rule that suddenly matches the whole book stops at
  // the cap and reports rather than actioning everyone.
  maxPerRun: number | null;
  requireApprovalForChanges: boolean;
}

export interface AutomationRule {
  id: string;
  name: string;
  description: string;
  category: AutomationCategory;
  ruleType: RuleType;
  triggerType: TriggerType;
  event: string;
  conditionGroups: RuleConditionGroup[];
  metActions: RuleAction[];
  exitActions: RuleAction[];
  scoreBands: ScoreBand[];
  runMode: RunMode;
  safety: RuleSafety;
  status: RuleStatus;
  membersMatched: number;
  lastTriggered: string;
  runs: RuleRun[];
  createdAt: string;
  updatedAt: string;
}

export const CATEGORIES: AutomationCategory[] = ['Uncategorised', 'Lifecycle', 'Behaviour', 'Value', 'Product', 'Seasonal'];

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------
// The documented set of WinePik Klaviyo metrics (help centre article 15866392).
// Metric names are Title Case and human-readable — the wp_ prefix belongs to
// event fields and profile properties, not to metric names.

export interface TriggerEvent {
  name: string;
  group: string;
  warning?: string;
}

export const TRIGGER_EVENTS: TriggerEvent[] = [{
  name: 'Subscription Created',
  group: 'New members and lifecycle'
}, {
  name: 'Subscription Paused',
  group: 'New members and lifecycle'
}, {
  name: 'Subscription Resumed',
  group: 'New members and lifecycle'
}, {
  name: 'Subscription Cancelled',
  group: 'New members and lifecycle'
}, {
  name: 'Subscription Plan Changed',
  group: 'Changes to a subscription'
}, {
  name: 'Subscription Frequency Changed',
  group: 'Changes to a subscription'
}, {
  name: 'Subscription Quantity Changed',
  group: 'Changes to a subscription'
}, {
  name: 'Subscription Address Updated',
  group: 'Changes to a subscription'
}, {
  name: 'Wine Swapped',
  group: 'Changes to a subscription'
}, {
  name: 'Wine Replaced',
  group: 'Changes to a subscription',
  warning: 'Wine Replaced only fires when “Notify customers of the change” is switched on during a product replace. If that toggle is off, this rule will not run at all.'
}, {
  name: 'Payment Method Updated',
  group: 'Changes to a subscription'
}, {
  name: 'Subscription Price Changed',
  group: 'Changes to a subscription'
}, {
  name: 'Order Upcoming',
  group: 'Orders'
}, {
  name: 'Order Skipped',
  group: 'Orders'
}, {
  name: 'Placed Order',
  group: 'Orders'
}, {
  name: 'Payment Failed',
  group: 'Payments and billing'
}, {
  name: 'Payment Retry Failed',
  group: 'Payments and billing'
}, {
  name: 'Payment Final Failure',
  group: 'Payments and billing'
}, {
  name: 'Payment Recovered',
  group: 'Payments and billing'
}, {
  name: 'Magic Link Sent',
  group: 'Member access'
}];

export const TRIGGER_EVENT_GROUPS = ['New members and lifecycle', 'Changes to a subscription', 'Orders', 'Payments and billing', 'Member access'];

export const getTriggerEvent = (name: string) => TRIGGER_EVENTS.find(e => e.name === name);

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------
// Actions divide into three kinds, and the distinction is a safety boundary,
// not a display convenience.
//
//   label   — changes how a member is described. Reversible, low blast radius.
//   surface — puts something in front of a human. Nothing changes for the member.
//   change  — alters the member's actual subscription. This is the difference
//             between a tagging tool and an automation platform, and every one
//             of these is capped, logged and confirmable.

export type ActionKind = 'label' | 'surface' | 'change';

export interface ActionDefinition {
  type: string;
  kind: ActionKind;
  group: string;
  description: string;
}

export const ACTION_DEFINITIONS: ActionDefinition[] = [{
  type: 'Add Tag',
  kind: 'label',
  group: 'Tagging',
  description: 'Applies a member tag. Syncs to Shopify and through to Klaviyo.'
}, {
  type: 'Remove Tag',
  kind: 'label',
  group: 'Tagging',
  description: 'Removes a member tag.'
}, {
  type: 'Create Task',
  kind: 'surface',
  group: 'Put in front of the team',
  description: 'Creates a task against the member, assigned to someone on the team.'
}, {
  type: 'Add Note to Contact',
  kind: 'surface',
  group: 'Put in front of the team',
  description: 'Writes an internal note on the contact. Not visible to the member.'
}, {
  type: 'Add to Action Required',
  kind: 'surface',
  group: 'Put in front of the team',
  description: 'Puts the member in the Action Required queue for someone to work through.'
}, {
  type: 'Flag as Moment to Reach Out',
  kind: 'surface',
  group: 'Put in front of the team',
  description: 'Surfaces the member in the “Worth a message” feed for a personal touch, rather than an automated email.'
}, {
  type: 'Send Klaviyo Event',
  kind: 'surface',
  group: 'Messaging',
  description: 'Fires the Automation Rule Triggered metric so a Klaviyo flow can pick it up.'
}, {
  type: 'Pause Subscription',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Pauses the member’s subscription.'
}, {
  type: 'Resume Subscription',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Resumes a paused subscription.'
}, {
  type: 'Skip Next Order',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Skips the member’s next scheduled order.'
}, {
  type: 'Change Frequency',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Moves the member onto a different billing frequency.'
}, {
  type: 'Replace Product',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Swaps a wine in the member’s subscription for another.'
}, {
  type: 'Add to Allocation',
  kind: 'change',
  group: 'Change the subscription',
  description: 'Makes the member eligible for a limited-release allocation.'
}];

export const ACTION_GROUPS = ['Tagging', 'Put in front of the team', 'Messaging', 'Change the subscription'];
export const ACTION_TYPES = ACTION_DEFINITIONS.map(a => a.type);
export const getActionDefinition = (type: string) => ACTION_DEFINITIONS.find(a => a.type === type);
export const isChangeAction = (type: string) => getActionDefinition(type)?.kind === 'change';

// Rules do not send arbitrary metric names. A free-text field let a merchant
// type an existing WinePik metric (Payment Failed, Placed Order) and spoof a
// real system event into their own flows. Every rule sends this single metric,
// carrying the rule name and category as properties.
export const RULE_KLAVIYO_METRIC = 'Automation Rule Triggered';

// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
// Portal-activity fields are held back until portal login tracking is confirmed
// — At-Risk Detection depends on the same data, so one answer unblocks both.

export const CONDITION_FIELDS: Record<string, string[]> = {
  Lifecycle: ['Order Count', 'Subscription Status', 'Member Tenure', 'Plan Name', 'Signup Date', 'Subscription Anniversary'],
  Behaviour: ['Box Edits Last 90d', 'Skips Last 90d', 'Consecutive Skips'],
  Value: ['Lifetime Spend', 'Average Order Value', 'Spend Trend', 'Current Plan Price'],
  Product: ['Top Varietal % (Red)', 'Top Varietal % (White)', 'Varietals Tried Count', 'Last Product Swapped', 'Most Ordered Product'],
  'Payment health': ['Failed Payments Last 90d', 'Card Expiry', 'Payment Method Status'],
  Tags: ['Has Tag'],
  Time: ['Days Since Last Order', 'Days Until Next Billing', 'Month of Year', 'Day of Week']
};

export const PLAN_OPTIONS = ['6 Bottles — Quarterly', '6 Bottles — Every 6 Months', '12 Bottles — Quarterly', '12 Bottles — Every 6 Months', '12 Bottles — Annually'];

export const PRODUCT_OPTIONS = ['2024 Shiraz', '2024 Cabernet Sauvignon', '2025 Chardonnay', '2025 Fiano', '2025 Pinot Gris', '2023 Grenache', 'NV Sparkling Blanc'];

export const DAY_OPTIONS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const MONTH_OPTIONS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const FREQUENCY_OPTIONS = ['Monthly', 'Every 2 months', 'Quarterly', 'Every 6 months', 'Annually'];

export const ALLOCATION_OPTIONS = ['2024 Single Vineyard Release', 'Museum Release — Winter', 'Library Reserve'];

export const ASSIGNEE_OPTIONS = ['Team', 'Sarah', 'Mike', 'Alex'];

// Saved Views shipped in V1.4 and its filter vocabulary is the same vocabulary
// as rule conditions. "Turn this view into a rule" is how merchants discover
// rules — they are already filtering to the members they care about.
export interface SavedViewSeed {
  id: string;
  name: string;
  surface: string;
  conditions: RuleCondition[];
}

export const SAVED_VIEWS: SavedViewSeed[] = [{
  id: 'sv-1',
  name: 'Lapsed over 6 months',
  surface: 'Subscriptions',
  conditions: [{
    id: 'sv1c1',
    field: 'Days Since Last Order',
    operator: 'is greater than',
    value: '180'
  }]
}, {
  id: 'sv-2',
  name: 'Big spenders',
  surface: 'Contacts',
  conditions: [{
    id: 'sv2c1',
    field: 'Lifetime Spend',
    operator: 'is greater than',
    value: '2000'
  }]
}, {
  id: 'sv-3',
  name: 'Skipped twice this quarter',
  surface: 'Subscriptions',
  conditions: [{
    id: 'sv3c1',
    field: 'Skips Last 90d',
    operator: 'is greater than or equal to',
    value: '2'
  }]
}, {
  id: 'sv-4',
  name: 'Cards expiring soon',
  surface: 'Subscriptions',
  conditions: [{
    id: 'sv4c1',
    field: 'Card Expiry',
    operator: 'is within last',
    value: '30',
    unit: 'days'
  }]
}];

// Mock members for the preview drawer. Merchants will not switch on a rule that
// touches 156 people without seeing who those people are.
export interface PreviewMember {
  id: string;
  name: string;
  email: string;
  plan: string;
  tenure: string;
  lifetimeValue: string;
  score?: number;
}

export const PREVIEW_MEMBERS: PreviewMember[] = [{
  id: 'm1',
  name: 'Sarah Johnson',
  email: 'sarah.johnson@example.com',
  plan: '12 Bottles — Quarterly',
  tenure: '2 yr 4 mo',
  lifetimeValue: '$3,240',
  score: 78
}, {
  id: 'm2',
  name: 'James Wilson',
  email: 'j.wilson@example.com',
  plan: '6 Bottles — Every 6 Months',
  tenure: '1 yr 1 mo',
  lifetimeValue: '$1,180',
  score: 64
}, {
  id: 'm3',
  name: 'Emma Thompson',
  email: 'emma.t@example.com',
  plan: '12 Bottles — Every 6 Months',
  tenure: '3 yr 8 mo',
  lifetimeValue: '$5,910',
  score: 41
}, {
  id: 'm4',
  name: 'Alice Johnson',
  email: 'alice.j@example.com',
  plan: '12 Bottles — Quarterly',
  tenure: '11 mo',
  lifetimeValue: '$1,640',
  score: 82
}, {
  id: 'm5',
  name: 'Robert Fox',
  email: 'r.fox@example.com',
  plan: '6 Bottles — Quarterly',
  tenure: '4 yr 2 mo',
  lifetimeValue: '$6,720',
  score: 33
}, {
  id: 'm6',
  name: 'Priya Nair',
  email: 'priya.nair@example.com',
  plan: '12 Bottles — Annually',
  tenure: '1 yr 9 mo',
  lifetimeValue: '$2,460',
  score: 57
}, {
  id: 'm7',
  name: 'Tom Alderton',
  email: 't.alderton@example.com',
  plan: '6 Bottles — Quarterly',
  tenure: '7 mo',
  lifetimeValue: '$720',
  score: 69
}, {
  id: 'm8',
  name: 'Grace Mullane',
  email: 'g.mullane@example.com',
  plan: '12 Bottles — Quarterly',
  tenure: '5 yr 1 mo',
  lifetimeValue: '$8,930',
  score: 22
}];

// ---------------------------------------------------------------------------
// Seed rules
// ---------------------------------------------------------------------------

const g = (id: string, logic: ConditionLogic, conditions: RuleCondition[]): RuleConditionGroup => ({
  id,
  logic,
  conditions
});

export const DEFAULT_SAFETY: RuleSafety = {
  maxPerRun: 250,
  requireApprovalForChanges: true
};

export const DEFAULT_SCORE_BANDS: ScoreBand[] = [{
  id: 'b1',
  label: 'Healthy',
  min: 0,
  max: 39,
  colour: 'green',
  actions: []
}, {
  id: 'b2',
  label: 'Monitor',
  min: 40,
  max: 59,
  colour: 'amber',
  actions: []
}, {
  id: 'b3',
  label: 'At Risk',
  min: 60,
  max: 79,
  colour: 'orange',
  actions: [{
    id: 'ba1',
    type: 'Add Tag',
    value: 'Needs Attention'
  }]
}, {
  id: 'b4',
  label: 'Critical',
  min: 80,
  max: 100,
  colour: 'red',
  actions: [{
    id: 'ba2',
    type: 'Add to Action Required',
    value: 'Critical churn risk'
  }]
}];

const base: Omit<AutomationRule, 'id' | 'name'> = {
  description: '',
  category: 'Uncategorised',
  ruleType: 'match',
  triggerType: 'Scheduled (Daily)',
  event: '',
  conditionGroups: [],
  metActions: [],
  exitActions: [],
  scoreBands: [],
  runMode: 'once',
  safety: DEFAULT_SAFETY,
  status: 'active',
  membersMatched: 0,
  lastTriggered: 'Never',
  runs: [],
  createdAt: '',
  updatedAt: ''
};

const mk = (r: Partial<AutomationRule> & {
  id: string;
  name: string;
}): AutomationRule => ({
  ...base,
  ...r
});

export const INITIAL_RULES: AutomationRule[] = [mk({
  id: '1',
  name: 'First-Time Member',
  description: 'Tags a member on their first order and removes the tag once they order again.',
  category: 'Lifecycle',
  triggerType: 'Event-based',
  event: 'Placed Order',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Order Count',
    operator: 'equals',
    value: '1'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: 'New Customer'
  }],
  exitActions: [{
    id: 'e1',
    type: 'Remove Tag',
    value: 'New Customer'
  }],
  membersMatched: 24,
  lastTriggered: '2 hours ago',
  runs: [{
    id: 'r1',
    date: '7 Aug 2026, 6:00am',
    mode: 'live',
    matched: 24,
    actioned: 3,
    heldByCap: 0
  }, {
    id: 'r2',
    date: '6 Aug 2026, 6:00am',
    mode: 'live',
    matched: 22,
    actioned: 2,
    heldByCap: 0
  }],
  createdAt: '12 Mar 2026',
  updatedAt: '2 Aug 2026'
}), mk({
  id: '2',
  name: 'At-Risk Score',
  description: 'Weighted churn-risk score across skips, payment health and engagement. Bands drive the response.',
  category: 'Behaviour',
  ruleType: 'scored',
  triggerType: 'Scheduled (Daily)',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Skips Last 90d',
    operator: 'is greater than or equal to',
    value: '2',
    weight: 30
  }, {
    id: 'c2',
    field: 'Failed Payments Last 90d',
    operator: 'is greater than or equal to',
    value: '1',
    weight: 25
  }, {
    id: 'c3',
    field: 'Days Since Last Order',
    operator: 'is greater than',
    value: '120',
    weight: 25
  }, {
    id: 'c4',
    field: 'Spend Trend',
    operator: 'is',
    value: 'Down',
    weight: 20
  }])],
  scoreBands: DEFAULT_SCORE_BANDS,
  runMode: 'every',
  membersMatched: 18,
  lastTriggered: '1 hour ago',
  runs: [{
    id: 'r1',
    date: '7 Aug 2026, 6:00am',
    mode: 'live',
    matched: 18,
    actioned: 18,
    heldByCap: 0
  }],
  createdAt: '4 Apr 2026',
  updatedAt: '28 Jul 2026'
}), mk({
  id: '3',
  name: 'Loyal Member (12+ months)',
  description: 'Recognises members who have been subscribed for a year or more.',
  category: 'Lifecycle',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Member Tenure',
    operator: 'is greater than',
    value: '12',
    unit: 'months'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: 'Loyal'
  }, {
    id: 'a2',
    type: 'Send Klaviyo Event',
    value: RULE_KLAVIYO_METRIC
  }],
  membersMatched: 156,
  lastTriggered: 'Yesterday',
  createdAt: '2 Feb 2026',
  updatedAt: '19 Jun 2026'
}), mk({
  id: '4',
  name: 'Pre-billing Stock Check',
  description: 'Flags upcoming orders so the team can check stock before the run.',
  category: 'Lifecycle',
  triggerType: 'Event-based',
  event: 'Order Upcoming',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Subscription Status',
    operator: 'is',
    value: 'Active'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add to Action Required',
    value: 'Check stock before billing'
  }],
  runMode: 'every',
  membersMatched: 64,
  lastTriggered: '4 hours ago',
  createdAt: '15 May 2026',
  updatedAt: '15 May 2026'
}), mk({
  id: '5',
  name: 'Anniversary Approaching',
  description: 'Surfaces members hitting their signup anniversary so someone can send a personal note.',
  category: 'Seasonal',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Subscription Anniversary',
    operator: 'is within last',
    value: '14',
    unit: 'days'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Flag as Moment to Reach Out',
    value: 'Anniversary coming up'
  }],
  runMode: 'every',
  membersMatched: 8,
  lastTriggered: 'Today',
  createdAt: '1 Jun 2026',
  updatedAt: '1 Jun 2026'
}), mk({
  id: '6',
  name: 'Auto-skip on repeated failure',
  description: 'Skips the next order for members whose final payment attempt failed, so the run does not carry a known bad card.',
  category: 'Value',
  triggerType: 'Event-based',
  event: 'Payment Final Failure',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Failed Payments Last 90d',
    operator: 'is greater than or equal to',
    value: '2'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Skip Next Order',
    value: 'Next scheduled order'
  }, {
    id: 'a2',
    type: 'Add to Action Required',
    value: 'Card needs updating'
  }],
  safety: {
    maxPerRun: 50,
    requireApprovalForChanges: true
  },
  status: 'testing',
  membersMatched: 11,
  lastTriggered: 'Today (test)',
  runs: [{
    id: 'r1',
    date: '7 Aug 2026, 6:00am',
    mode: 'test',
    matched: 11,
    actioned: 0,
    heldByCap: 0
  }, {
    id: 'r2',
    date: '6 Aug 2026, 6:00am',
    mode: 'test',
    matched: 9,
    actioned: 0,
    heldByCap: 0
  }],
  createdAt: '1 Aug 2026',
  updatedAt: '6 Aug 2026'
}), mk({
  id: '7',
  name: 'Red Wine Lover',
  description: 'Tags members whose selections skew heavily red.',
  category: 'Product',
  triggerType: 'Event-based',
  event: 'Wine Swapped',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Top Varietal % (Red)',
    operator: 'is greater than',
    value: '70'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: 'Premium'
  }],
  exitActions: [{
    id: 'e1',
    type: 'Remove Tag',
    value: 'Premium'
  }],
  membersMatched: 89,
  lastTriggered: 'Yesterday',
  createdAt: '21 Feb 2026',
  updatedAt: '30 Jul 2026'
}), mk({
  id: '8',
  name: 'High Value Spender',
  description: 'Identifies members above the lifetime spend threshold.',
  category: 'Value',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Lifetime Spend',
    operator: 'is greater than',
    value: '2000'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: 'High Value'
  }],
  membersMatched: 42,
  lastTriggered: '3 hours ago',
  createdAt: '9 Jan 2026',
  updatedAt: '9 Jan 2026'
}), mk({
  id: '9',
  name: 'Allocation eligibility — Library Reserve',
  description: 'Makes long-tenured, high-value members eligible for the Library Reserve release.',
  category: 'Product',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Member Tenure',
    operator: 'is greater than',
    value: '24',
    unit: 'months'
  }, {
    id: 'c2',
    field: 'Lifetime Spend',
    operator: 'is greater than',
    value: '3000'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add to Allocation',
    value: 'Library Reserve'
  }],
  safety: {
    maxPerRun: 100,
    requireApprovalForChanges: true
  },
  status: 'paused',
  membersMatched: 37,
  lastTriggered: '2 weeks ago',
  createdAt: '7 Jan 2026',
  updatedAt: '24 Jul 2026'
}), mk({
  id: '10',
  name: 'Sparkling Fan',
  description: 'Members whose most ordered wine is sparkling.',
  category: 'Product',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Most Ordered Product',
    operator: 'is',
    value: 'NV Sparkling Blanc'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: 'Premium'
  }],
  status: 'paused',
  membersMatched: 23,
  lastTriggered: '1 week ago',
  createdAt: '11 Mar 2026',
  updatedAt: '31 Jul 2026'
}), mk({
  id: '11',
  name: 'Win-back — lapsed 6 months',
  category: 'Uncategorised',
  conditionGroups: [g('g1', 'AND', [{
    id: 'c1',
    field: 'Days Since Last Order',
    operator: 'is greater than',
    value: '180'
  }])],
  metActions: [{
    id: 'a1',
    type: 'Add Tag',
    value: ''
  }],
  status: 'draft',
  createdAt: '5 Aug 2026',
  updatedAt: '5 Aug 2026'
})];

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------
// Templates pre-fill the whole rule but deliberately leave the tag empty — they
// must not invent tag names in a merchant's store.

export interface RuleTemplate {
  key: string;
  icon: string;
  name: string;
  description: string;
  payload: Partial<AutomationRule>;
}

export const TEMPLATES: RuleTemplate[] = [{
  key: 'welcome',
  icon: 'Sprout',
  name: 'New Member Welcome',
  description: 'Tag first-time subscribers and remove after their second order.',
  payload: {
    name: 'New Member Welcome',
    description: 'Tags a member on their first order and removes the tag once they order again.',
    category: 'Lifecycle',
    ruleType: 'match',
    triggerType: 'Event-based',
    event: 'Placed Order',
    runMode: 'once',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Order Count',
      operator: 'equals',
      value: '1'
    }])],
    metActions: [{
      id: 'ta1',
      type: 'Add Tag',
      value: ''
    }],
    exitActions: [{
      id: 'te1',
      type: 'Remove Tag',
      value: ''
    }]
  }
}, {
  key: 'at-risk',
  icon: 'AlertTriangle',
  name: 'At-Risk Score',
  description: 'Weighted churn-risk score with bands from Healthy through to Critical.',
  payload: {
    name: 'At-Risk Score',
    description: 'Weighted churn-risk score across skips, payment health and engagement.',
    category: 'Behaviour',
    ruleType: 'scored',
    triggerType: 'Scheduled (Daily)',
    event: '',
    runMode: 'every',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Skips Last 90d',
      operator: 'is greater than or equal to',
      value: '2',
      weight: 30
    }, {
      id: 't2',
      field: 'Failed Payments Last 90d',
      operator: 'is greater than or equal to',
      value: '1',
      weight: 25
    }, {
      id: 't3',
      field: 'Days Since Last Order',
      operator: 'is greater than',
      value: '120',
      weight: 25
    }, {
      id: 't4',
      field: 'Spend Trend',
      operator: 'is',
      value: 'Down',
      weight: 20
    }])],
    scoreBands: DEFAULT_SCORE_BANDS
  }
}, {
  key: 'prebilling',
  icon: 'CalendarClock',
  name: 'Pre-billing Stock Check',
  description: 'Catch upcoming orders before the run so the team can check stock.',
  payload: {
    name: 'Pre-billing Stock Check',
    description: 'Flags upcoming orders so the team can check stock before the billing run.',
    category: 'Lifecycle',
    ruleType: 'match',
    triggerType: 'Event-based',
    event: 'Order Upcoming',
    runMode: 'every',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Subscription Status',
      operator: 'is',
      value: 'Active'
    }])],
    metActions: [{
      id: 'ta1',
      type: 'Add to Action Required',
      value: 'Check stock before billing'
    }],
    exitActions: []
  }
}, {
  key: 'loyalty',
  icon: 'Trophy',
  name: 'Loyalty Recognition',
  description: 'Tag members who reach 12-month and 24-month milestones.',
  payload: {
    name: 'Loyalty Recognition',
    description: 'Recognises members who have been subscribed for a year or more.',
    category: 'Lifecycle',
    ruleType: 'match',
    triggerType: 'Scheduled (Daily)',
    event: '',
    runMode: 'once',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Member Tenure',
      operator: 'is greater than',
      value: '12',
      unit: 'months'
    }])],
    metActions: [{
      id: 'ta1',
      type: 'Add Tag',
      value: ''
    }],
    exitActions: []
  }
}, {
  key: 'moments',
  icon: 'HeartHandshake',
  name: 'Anniversary Moment',
  description: 'Surface members hitting an anniversary for a personal message, not an automated one.',
  payload: {
    name: 'Anniversary Moment',
    description: 'Surfaces members hitting their signup anniversary so someone can send a personal note.',
    category: 'Seasonal',
    ruleType: 'match',
    triggerType: 'Scheduled (Daily)',
    event: '',
    runMode: 'every',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Subscription Anniversary',
      operator: 'is within last',
      value: '14',
      unit: 'days'
    }])],
    metActions: [{
      id: 'ta1',
      type: 'Flag as Moment to Reach Out',
      value: 'Anniversary coming up'
    }],
    exitActions: []
  }
}, {
  key: 'varietal',
  icon: 'Wine',
  name: 'Varietal Preferences',
  description: 'Auto-tag based on red, white, or mixed wine selections.',
  payload: {
    name: 'Varietal Preferences',
    description: 'Tags members whose selections skew heavily to one style.',
    category: 'Product',
    ruleType: 'match',
    triggerType: 'Event-based',
    event: 'Wine Swapped',
    runMode: 'once',
    conditionGroups: [g('g1', 'AND', [{
      id: 't1',
      field: 'Top Varietal % (Red)',
      operator: 'is greater than',
      value: '70'
    }])],
    metActions: [{
      id: 'ta1',
      type: 'Add Tag',
      value: ''
    }],
    exitActions: [{
      id: 'te1',
      type: 'Remove Tag',
      value: ''
    }]
  }
}];
